import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Divider,
  Modal,
} from "@bca/ui-library";
import styled from "styled-components";

import CountdownTimer from "../CountdownTimer";
import TermsAndConditions from "./TermsAndConditions";
import Spinner from "../Spinner";

import { formatMoney } from "./formatMoney";
import { getTimeUntilSaleEnds } from "../../helpers/getTimeUntilSaleEnds";
import trackVehicle from "../../api/trackVehicle";

interface BidNowModalProps {
  reserveMet: boolean;
  bidAmount: number;
  currentBid: number;
  saleEndDate: string;
  isOpen: boolean;
  termsAndConditionsNeedAccepting: boolean;
  termsAndConditionsLink: string;
  isTracked: boolean;
  vrm: string | string[];
  onClose: () => void;
  onPlaceBid: () => Promise<void>;
  onAcceptTC: () => Promise<void>;
  setIsTracked: (value: boolean) => void;
  inventoryId: string | null;
  isToUseAuthZero?: boolean;
  accessToken?: string;
}

const ReserveMet = styled(Box)`
  border-radius: 4px;
`;

const ReserveNotMet = styled(Box)`
  border: 1px solid #64798c;
  border-radius: 4px;
`;

const BidNowModal: React.FunctionComponent<BidNowModalProps> = ({
  reserveMet,
  bidAmount,
  currentBid,
  saleEndDate,
  isOpen,
  termsAndConditionsLink,
  termsAndConditionsNeedAccepting,
  isTracked,
  vrm,
  onClose,
  onPlaceBid,
  onAcceptTC,
  setIsTracked,
  inventoryId,
  isToUseAuthZero,
  accessToken,
}) => {
  const showCountdownTimer = getTimeUntilSaleEnds(saleEndDate) <= 60;
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const screenLowerThan768 =
    typeof window !== "undefined" ? window.innerWidth < 768 : false;

  const handleConfirm = () => {
    setLoading(true);
    if (termsAndConditionsNeedAccepting) {
      onAcceptTC().then(() => {
        onPlaceBid().then(async () => {
          if (!isTracked) {
            const result = await trackVehicle(vrm, inventoryId, isToUseAuthZero, accessToken);

            if (result.status > 200 || result?.degradedServices?.length > 0) {
              setIsTracked(false);
            } else {
              setIsTracked(true);
            }
          }
          setLoading(false);
          onClose();
        });
      });
      return;
    }

    onPlaceBid().then(async () => {
      if (!isTracked) {
        const result = await trackVehicle(vrm, inventoryId, isToUseAuthZero, accessToken);

        if (result.status > 200 || result?.degradedServices?.length > 0) {
          setIsTracked(false);
        } else {
          setIsTracked(true);
        }
      }
      setLoading(false);
      onClose();
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      role="dialog"
      onClose={onClose}
      aria-label="bid now modal"
    >
      <Box
        display="flex"
        flexDirection={["column", "row"]}
        justifyContent="space-between"
        mb={[4, 30]}
      >
        <Box display="flex" flexDirection="column" width={["auto", "45%"]}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h6" m={0}>
              Current Bid
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Typography
              css={`
                font-size: 30px;
                font-weight: 500;
              `}
              m={0}
              mr={4}
            >
              {formatMoney(currentBid)}
            </Typography>
            {reserveMet && (
              <ReserveMet
                backgroundColor="grey-blue"
                height={26}
                py={0}
                px={"10px"}
              >
                <Typography
                  variant="caption"
                  color="primary-white"
                  m={0}
                  css={`
                    font-size: 13px;
                    line-height: 24px;
                  `}
                >
                  Reserve met
                </Typography>
              </ReserveMet>
            )}
            {!reserveMet && (
              <ReserveNotMet
                backgroundColor="white"
                height={26}
                py={0}
                px={"10px"}
              >
                <Typography
                  variant="caption"
                  color="grey-blue"
                  m={0}
                  css={`
                    font-size: 13px;
                    line-height: 24px;
                  `}
                >
                  Reserve not met
                </Typography>
              </ReserveNotMet>
            )}
          </Box>
          {showCountdownTimer && <CountdownTimer saleEndDate={saleEndDate} />}
        </Box>
        <Box display="flex">
          <Divider
            orientation={screenLowerThan768 ? "horizontal" : "vertical"}
            width={screenLowerThan768 ? "100%" : "1px"}
            marginBottom={[30, 0]}
            marginTop={[30, 0]}
            marginLeft={0}
            marginRight={0}
          />
        </Box>
        <Box display="flex" flexDirection="column" width={["auto", "45%"]}>
          <Typography variant="h6" m={0}>
            Confirm your Proxy Bid
          </Typography>
          <Typography
            css={`
              font-size: 30px;
              font-weight: 500;
            `}
            m={0}
          >
            {formatMoney(bidAmount)}
          </Typography>
        </Box>
      </Box>
      <Alert
        paddingX={30}
        paddingY={24}
        width={[310, 640]}
        title="By pressing Confirm, you commit to buy this vehicle if you are the
        winning bidder."
      ></Alert>
      {termsAndConditionsNeedAccepting && (
        <TermsAndConditions
          checked={checked}
          setChecked={setChecked}
          termsAndConditionsLink={termsAndConditionsLink}
        />
      )}
      <Box display="flex" justifyContent="space-between" mt={30}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary-gradient"
          onClick={handleConfirm}
          disabled={loading || (termsAndConditionsNeedAccepting && !checked)}
          minWidth={120}
          data-en="click"
          data-ec="button"
          data-el="bidnow"
          data-ea="confirmation"
          data-es=""
          data-ev={bidAmount}
        >
          {loading ? <Spinner /> : "Confirm"}
        </Button>
      </Box>
    </Modal>
  );
};

export default BidNowModal;
